import { Button } from '@/components/ui/button';
import { Trans, useTranslation } from 'react-i18next';
import { Info } from '@/components/icons/barrel';
import { AccountOption } from '../utils/vest-capital-table.types';
import { cn } from '@/utils/cn';
import { tableRows } from './vest-capital-table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface DesktopAccountTableProps {
  accountOptions: AccountOption[];
  onButtonClick: (priceId: string) => void;
  activePlanPriceId?: string | null;
  isEval: boolean;
  isMissingEmail: boolean;
  hasEval: boolean;
  isLoading: boolean;
}

export function DesktopAccountTable({
  accountOptions,
  onButtonClick,
  activePlanPriceId = null,
  isEval,
  isMissingEmail,
  hasEval,
  isLoading,
}: DesktopAccountTableProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-6">
      {!!accountOptions[0] && (
        <div className="text-center text-lg text-muted-foreground">
          <Trans
            i18nKey="vestCapital.allPlansShare"
            components={{
              bold: <span className="font-semibold text-foreground" />,
            }}
            values={{
              maxDailyLoss: accountOptions[0].maxDailyLoss,
              leverage: accountOptions[0].leverage,
            }}
          />
        </div>
      )}

      <table className="w-full font-normal">
        <thead>
          <tr>
            <th></th>

            {accountOptions.map((option, index) => {
              const isActive = hasEval && activePlanPriceId === option.priceId;

              return (
                <th
                  key={index}
                  className={cn(
                    'rounded-3xl font-normal',
                    isActive && 'glass-yellow',
                  )}
                >
                  <div className="flex flex-col items-start p-5 lg:min-w-[184px]">
                    <div className="text-xl capitalize">{option.title}</div>
                    <div className="mt-3 text-5xl font-medium">
                      {option.size}
                    </div>
                    <div className="mt-1 capitalize text-vestgrey-500">
                      Trading Capital
                    </div>

                    <Button
                      variant={isActive ? 'yellow' : 'outline'}
                      size="xl"
                      className="mt-7 w-full capitalize"
                      disabled={isLoading || isMissingEmail || hasEval}
                      onClick={() => onButtonClick(option.priceId)}
                    >
                      {isActive
                        ? t('vestCapital.currentPlan')
                        : t('vestCapital.startNow')}
                    </Button>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {tableRows
            .filter((row) => !row.mobileOnly)
            .map((row) => (
              <tr key={row.key}>
                <td className="w-[22%] pr-5 pt-5">
                  <div className="flex items-center gap-2 text-xl capitalize text-vestgrey-500">
                    {t(row.labelKey)}

                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger>
                          <Info
                            size={14}
                            className="cursor-pointer text-vestgrey-500 hover:text-vestgrey-900"
                          />
                        </TooltipTrigger>
                        <TooltipContent className="normal-case">
                          {t(row.tooltipKey)}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </td>

                {accountOptions.map((option, index) => (
                  <td key={index} className="w-[26%] px-5 pt-5 text-xl">
                    {option[row.key]}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}
